import { Lock, Save, X, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";
import { toast } from "@/hooks/useToast";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useI18n } from '@/hooks/useI18n';
import PasswordFields, { isPasswordValid, type PasswordData } from "@/components/ui/password-fields";

// Security section component with separate edit state
interface SecuritySectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
  isSaving?: boolean;
  onSave?: () => void;
}

const SecuritySection = ({ 
  isEditing = false,
  onEditToggle,
  onCancelEdit,
  isSaving = false,
  onSave
}: SecuritySectionProps) => {
  const [passwordData, setPasswordData] = useState<PasswordData>({ password: '', confirmPassword: '' });
  
  // Get updatePassword from hook
  const { updatePassword } = useUserProfile();
  const { t } = useI18n();

  const allValid = isPasswordValid(passwordData);

  const handleCancel = () => {
    // Reset password fields
    setPasswordData({ password: '', confirmPassword: '' });
    onCancelEdit?.();
  };

  const handleSavePassword = async () => {
    if (!passwordData.password.trim()) {
      toast({
        title: t('common.error'),
        description: t('security.enterNewPassword'),
        variant: "destructive",
      });
      return;
    }
    
    // Check if all requirements are met
    if (!allValid) {
      toast({
        title: t('common.error'),
        description: t('security.passwordNotMeetRequirements'),
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Update password using auth context
      const result = await updatePassword(passwordData.password);

      if (!result.success) {
        console.error('Error updating password:', result.error);
        
        // Handle specific error cases
        if (result.error?.includes('Riautenticazione richiesta')) {
          toast({
            title: t('security.reauthenticateTitle'),
            description: t('security.reauthenticateDesc'),
            variant: "destructive",
          });
          // Redirect to login page
          window.location.href = '/login';
          return;
        }
        
        toast({
          title: t('common.error'),
          description: result.error || t('security.passwordUpdateError'),
          variant: "destructive",
        });
        return;
      }
      
      // Success
      toast({
        title: t('common.success'),
        description: t('security.passwordUpdatedSuccess'),
      });
      
      // Clear password fields after successful save
      setPasswordData({ password: '', confirmPassword: '' });
      
      // Call the parent save handler
      if (onSave) {
        onSave();
      }
      
    } catch (error) {
      console.error('Unexpected error updating password:', error);
      toast({
        title: t('common.error'),
        description: t('security.passwordUpdateUnexpected'),
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-normal flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {t("security.title")}
          </CardTitle>
          {onEditToggle && onCancelEdit && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={handleSavePassword}
                    disabled={!passwordData.password.trim() || isSaving || !allValid}
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isSaving ? t("common.saving") : t("common.save")}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onEditToggle}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  {t("common.edit")}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <PasswordFields
            value={passwordData}
            onChange={setPasswordData}
            disabled={isSaving}
          />
        ) : (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t("security.passwordLabel")}
            </label>
            <div className="text-sm text-gray-500">••••••••••••••••</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecuritySection;
